import React, { useEffect } from 'react';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { visitorAPI } from './services/api';
import Header from './components/Header';
import Footer from './components/Footer';

import ScrollToTop from './components/ScrollToTop';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import AuthPopup from './components/AuthPopup';
import ProtectedRoute from './components/ProtectedRoute'; // Import ProtectedRoute
import WhatsAppButton from './components/WhatsAppButton'; // Import WhatsAppButton

// User Pages
import Home from './pages/user/Home';
import Blogs from './pages/user/Blogs';
import BlogDetail from './pages/user/BlogDetail'; // Import BlogDetail
import OurApp from './pages/user/OurApp';
import Solutions from './pages/user/Solutions';
import About from './pages/user/About';
import Projects from './pages/user/Projects';
import Products from './pages/user/Products';
import AddressBook from './pages/user/AddressBook';
import Wishlist from './pages/user/Wishlist';
import OrderTracking from './pages/user/OrderTracking'; // Import the new page
import Services from './pages/user/Services';
import Contact from './pages/user/Contact';
import Orders from './pages/user/Orders';
import CropProtection from './pages/user/services/CropProtection';
import ExpertConsultancy from './pages/user/services/ExpertConsultancy';
import PrecisionAgriculture from './pages/user/services/PrecisionAgriculture';
import FarmManagement from './pages/user/services/FarmManagement';
import Hydroponics from './pages/user/services/Hydroponics';

// Solution Pages
import FarmgetApp from './pages/user/solutions/FarmgetApp';
import QualityInputs from './pages/user/solutions/QualityInputs';
import PrecisionFarming from './pages/user/solutions/PrecisionFarming';
import ResearchInnovation from './pages/user/solutions/ResearchInnovation';
import HydroponicsSolution from './pages/user/solutions/Hydroponics';
import Landscaping from './pages/user/solutions/Landscaping';
import NaturalFarming from './pages/user/solutions/NaturalFarming';
import PostHarvest from './pages/user/solutions/PostHarvest';
import Procurement from './pages/user/solutions/Procurement';
import FoodProcessing from './pages/user/solutions/FoodProcessing';
import TermsAndConditions from './pages/user/TermsAndConditions';
import PrivacyPolicy from './pages/user/PrivacyPolicy';

// Admin Pages
import Dashboard from './pages/Admin/Dashboard';
import Categories from './pages/Admin/Categories';
import Stocks from './pages/Admin/Stocks';
import Users from './pages/Admin/Users';
import Feedbacks from './pages/Admin/Feedbacks';
import AdminHome from './pages/Admin/Home';
import AdminBlogs from './pages/Admin/Blogs';
import AddEditBlog from './pages/Admin/AddEditBlog'; // Import AddEditBlog
import AdminApp from './pages/Admin/OurApp';
import AdminSolutions from './pages/Admin/Solutions';
import AdminProjects from './pages/Admin/Projects';
import AdminProducts from './pages/Admin/Products';
import AdminOrders from './pages/Admin/Orders';
import AdminServices from './pages/Admin/Services';
import AdminContact from './pages/Admin/Contact';
import AdminCropProtection from './pages/Admin/services/CropProtection';
import AdminExpertConsultancy from './pages/Admin/services/ExpertConsultancy';
import AdminPrecisionAgriculture from './pages/Admin/services/PrecisionAgriculture';
import AdminFarmManagement from './pages/Admin/services/FarmManagement';
import AdminHydroponics from './pages/Admin/services/Hydroponics';

const AppContent = () => {
  const { user, setShowAuthPopup, loading, showAuthPopup } = useAuth();
  const location = useLocation();
  const [hasShownAuthPopup, setHasShownAuthPopup] = React.useState(false);

  // Reset the "shown" state when location changes
  useEffect(() => {
    setHasShownAuthPopup(false);
  }, [location]);

  useEffect(() => {
    // Track visitor
    const trackVisitor = async () => {
      try {
        await visitorAPI.trackVisit();
      } catch (error) {
        console.error("Error tracking visit:", error);
      }
    };
    trackVisitor();
  }, []);

  /* 
  useEffect(() => {
    if (!loading && !user && !showAuthPopup && !hasShownAuthPopup) {
      const path = location.pathname;
      // Define paths where login is NOT required immediately
      // '/', '/about' are allowed. 
      // Also need to allow '/admin' login page if it exists separately, but here admin pages are protected.
      // Let's implement the rule: "login ka pop up hena vo home or about ke page ke alava dusre pages me jaye tab aana chahiye"

      const isHomePage = path === '/';
      const isAboutPage = path === '/about';
      const isBlogsPage = path.startsWith('/blogs');
      const isContactPage = path === '/contact';

      // Only add listener if NOT on home/about/blogs/contact page
      if (!isHomePage && !isAboutPage && !isBlogsPage && !isContactPage) {
        const handleScroll = () => {
          // Trigger after scrolling down 700px (approx 30% of screen height)
          // or maybe just a bit like 100px to ensure they started interacting with content
          if (window.scrollY > 700) {
            setShowAuthPopup(true);
            setHasShownAuthPopup(true);
          }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
      }
    }
  }, [location, user, loading, setShowAuthPopup, showAuthPopup, hasShownAuthPopup]);
  */

  return (
    <>
      <ScrollToTop />
      <Header />

      <AuthPopup />
      <WhatsAppButton />
      <Routes>
        {/* User Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/blogs/:id" element={<BlogDetail />} />
        <Route path="/app" element={<OurApp />} />
        <Route path="/solutions" element={<Solutions />} />
        {/* Solution Routes */}
        <Route path="/solutions/farmget-app-consultancy" element={<FarmgetApp />} />
        <Route path="/solutions/quality-agricultural-inputs" element={<QualityInputs />} />
        <Route path="/solutions/protected-precision-farming" element={<PrecisionFarming />} />
        <Route path="/solutions/research-innovation-support" element={<ResearchInnovation />} />
        <Route path="/solutions/hydroponics-aeroponics" element={<HydroponicsSolution />} />
        <Route path="/solutions/landscaping-green-development" element={<Landscaping />} />
        <Route path="/solutions/natural-farming" element={<NaturalFarming />} />
        <Route path="/solutions/post-harvest-value-addition" element={<PostHarvest />} />
        <Route path="/solutions/agricultural-products-procurement" element={<Procurement />} />
        <Route path="/solutions/agriculture-food-processing" element={<FoodProcessing />} />

        <Route path="/projects" element={<Projects />} />
        <Route path="/products" element={<Products />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:id" element={<OrderTracking />} />
          <Route path="/my-addresses" element={<AddressBook />} />
          <Route path="/wishlist" element={<Wishlist />} />
        </Route>
        <Route path="/services" element={<Services />} />
        <Route path="/services/crop-protection" element={<CropProtection />} />
        <Route path="/services/expert-consultancy" element={<ExpertConsultancy />} />
        <Route path="/services/precision-agriculture" element={<PrecisionAgriculture />} />
        <Route path="/services/farm-management" element={<FarmManagement />} />
        <Route path="/services/hydroponics" element={<Hydroponics />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        {/* Admin Routes */}
        <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/dashboard" element={<Dashboard />} />
          <Route path="/admin/categories" element={<Categories />} />
          <Route path="/admin/stocks" element={<Stocks />} />
          <Route path="/admin/users" element={<Users />} />
          <Route path="/admin/feedbacks" element={<Feedbacks />} />
          <Route path="/admin/blogs" element={<AdminBlogs />} />
          <Route path="/admin/blogs/add" element={<AddEditBlog />} />
          <Route path="/admin/blogs/edit/:id" element={<AddEditBlog />} />
          <Route path="/admin/app" element={<AdminApp />} />
          <Route path="/admin/solutions" element={<AdminSolutions />} />
          <Route path="/admin/projects" element={<AdminProjects />} />
          <Route path="/admin/products" element={<AdminProducts />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/services" element={<AdminServices />} />
          <Route path="/admin/services/crop-protection" element={<AdminCropProtection />} />
          <Route path="/admin/services/expert-consultancy" element={<AdminExpertConsultancy />} />
          <Route path="/admin/services/precision-agriculture" element={<AdminPrecisionAgriculture />} />
          <Route path="/admin/services/farm-management" element={<AdminFarmManagement />} />
          <Route path="/admin/services/hydroponics" element={<AdminHydroponics />} />
          <Route path="/admin/contact" element={<AdminContact />} />
        </Route>
      </Routes>
      <Footer />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
